import { getFeature, setFeatureLogger } from "@asurion-core/expert-workspace-feature-flagging";
import { getLogger } from "@expert/logging";
import { useQuery } from "@tanstack/react-query";

import { env } from "@/config";
import { useAgentAttributesStore } from "@/sdk/agent/store/agent-attributes.store";

import { userCache } from "../auth";

const logger = getLogger({ module: "UseFeatureFlag" });

export interface UseFeatureFlagOptions<T> {
    cacheTtl?: number;
    isEnabled?: boolean;
    onResponse?: (flag: T | undefined) => void;
}

export const useFeatureFlag = <T = boolean>(
    featureFlag: string,
    context: Record<string, unknown> = {},
    { cacheTtl = 1800 /* 30 min */, isEnabled = true, onResponse }: UseFeatureFlagOptions<T> = {},
) => {
    // fall-back to identify (sub) if employeeId is undefined
    const employeeId = userCache.employeeId ?? userCache.identity;
    const agentSite = useAgentAttributesStore((state) => state.site);
    setFeatureLogger(logger);
    const { data, isLoading, isError } = useQuery({
        queryKey: [featureFlag, ...Object.values(context)],
        queryFn: async (): Promise<T | null> => {
            const result = await getFeature<T>(
                featureFlag,
                {
                    environment: env.mode,
                    channel: env.channel,
                    employeeId,
                    appName: "expert-workspace",
                    source: "expert-ui",
                    agentSite,
                    ...context,
                },
                cacheTtl,
            );
            onResponse?.(result);
            // React Query rejects an `undefined` return ("Query data cannot be undefined"). A missing
            // flag (e.g. a 404 for a key not yet in this env's config) makes getFeature return undefined,
            // so coalesce to null: consumers still get a falsy value via their `= defaultValue`/nullish
            // handling, and the code is safe to ship before its flag config is deployed.
            return result ?? null;
        },
        refetchOnMount: false,
        enabled: isEnabled,
    });
    // Map the internal `null` sentinel back to `undefined` so the public shape stays `T | undefined`
    // (unchanged for callers); `??` leaves legitimate falsy flag values like `false`/`0`/`""` intact.
    return { data: data ?? undefined, isLoading, isError };
};
